import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  share,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { errMessage } from '@ui';
import { ManualEventsActions } from '../state/manual-events/manual-events.actions';
import { manualEventsFeature } from '../state/manual-events/manual-events.feature';
import { ManualEventsService } from '../../services/manual-events.service';
import { SharedActions } from '../state/shared/shared.actions';

export const startLoadingManualEvents$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ManualEventsActions.startLoadingManualEvents),
      exhaustMap(({ search }) => [
        ManualEventsActions.setIsLoading({ isLoading: true }),
        ManualEventsActions.loadManualEvents({
          lastId: null,
          append: false,
          search: search ?? '',
        }),
      ]),
    ),
  { functional: true },
);

export const loadManualEvents$ = createEffect(
  (actions$ = inject(Actions), service = inject(ManualEventsService)) =>
    actions$.pipe(
      ofType(ManualEventsActions.loadManualEvents),
      switchMap(({ lastId, append, search }) =>
        service
          .getAll({ lastId: lastId ?? undefined, search: search || undefined })
          .pipe(
            switchMap((items) => [
              ManualEventsActions.loadManualEventsSuccess({ items, append }),
              ManualEventsActions.setIsLoading({ isLoading: false }),
              ManualEventsActions.setIsLoadingMore({ isLoadingMore: false }),
            ]),
            catchError((response: unknown) => [
              ManualEventsActions.loadManualEventsFail(),
              ManualEventsActions.setIsLoading({ isLoading: false }),
              ManualEventsActions.setIsLoadingMore({ isLoadingMore: false }),
              SharedActions.showMessage({
                error: errMessage(response as Error),
              }),
            ]),
          ),
      ),
    ),
  { functional: true },
);

export const loadMoreManualEvents$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ManualEventsActions.loadMoreManualEvents),
      withLatestFrom(
        store.select(manualEventsFeature.selectIsLastPage),
        store.select(manualEventsFeature.selectIsLoadingMore),
        store.select(manualEventsFeature.selectLastId),
        store.select(manualEventsFeature.selectSearch),
      ),
      filter(
        ([, isLastPage, isLoadingMore, lastId]) =>
          !isLastPage && !isLoadingMore && Boolean(lastId),
      ),
      exhaustMap(([, , , lastId, search]) => [
        ManualEventsActions.setIsLoadingMore({ isLoadingMore: true }),
        ManualEventsActions.loadManualEvents({
          lastId,
          append: true,
          search: search ?? '',
        }),
      ]),
    ),
  { functional: true },
);

export const startCreateManualEvent$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ManualEventsActions.startCreateManualEvent),
      exhaustMap(({ create }) => [
        ManualEventsActions.setIsSaving({ isSaving: true }),
        ManualEventsActions.createManualEvent({ create }),
      ]),
      share(),
    ),
  { functional: true },
);

export const createManualEvent$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ManualEventsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ManualEventsActions.createManualEvent),
      mergeMap(({ create }) =>
        service.create(create).pipe(
          switchMap((item) => [
            ManualEventsActions.createManualEventSuccess({ item }),
            ManualEventsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant(
                'TOASTS.MANUAL_EVENT_CREATED',
              ) as string,
            }),
          ]),
          catchError((response: unknown) => [
            ManualEventsActions.createManualEventFail(),
            ManualEventsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ error: errMessage(response as Error) }),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateManualEvent$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ManualEventsActions.startUpdateManualEvent),
      exhaustMap(({ id, update }) => [
        ManualEventsActions.setIsSaving({ isSaving: true }),
        ManualEventsActions.updateManualEvent({ id, update }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateManualEvent$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ManualEventsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ManualEventsActions.updateManualEvent),
      mergeMap(({ id, update }) =>
        service.update(id, update).pipe(
          switchMap(() => [
            ManualEventsActions.updateManualEventSuccess({ id, update }),
            ManualEventsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant(
                'TOASTS.MANUAL_EVENT_UPDATED',
              ) as string,
            }),
          ]),
          catchError((response: unknown) => [
            ManualEventsActions.updateManualEventFail(),
            ManualEventsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ error: errMessage(response as Error) }),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const toggleManualEventActive$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ManualEventsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ManualEventsActions.toggleManualEventActive),
      mergeMap(({ id, name, active }) =>
        service.update(id, { name, active }).pipe(
          switchMap(() => [
            ManualEventsActions.toggleManualEventActiveSuccess({ id, active }),
            SharedActions.showMessage({
              success: translate.instant(
                active ? 'TOASTS.EVENT_TYPE_ENABLED' : 'TOASTS.EVENT_TYPE_DISABLED',
                { name },
              ) as string,
            }),
          ]),
          catchError((response: unknown) => [
            ManualEventsActions.toggleManualEventActiveFail(),
            SharedActions.showMessage({ error: errMessage(response as Error) }),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const reorderManualEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    service = inject(ManualEventsService),
  ) =>
    actions$.pipe(
      ofType(ManualEventsActions.reorderManualEvents),
      withLatestFrom(store.select(manualEventsFeature.selectSearch)),
      mergeMap(([{ orderedIds }, search]) =>
        service.reorder(orderedIds).pipe(
          map(() => ManualEventsActions.reorderManualEventsSuccess()),
          catchError((response: unknown) => [
            ManualEventsActions.reorderManualEventsFail(),
            // Re-fetch to undo the optimistic reorder already applied to the store.
            ManualEventsActions.startLoadingManualEvents({ search: search ?? '' }),
            SharedActions.showMessage({ error: errMessage(response as Error) }),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startDeleteManualEvent$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ManualEventsActions.startDeleteManualEvent),
      exhaustMap(({ id }) => [ManualEventsActions.deleteManualEvent({ id })]),
    ),
  { functional: true },
);

export const deleteManualEvent$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ManualEventsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ManualEventsActions.deleteManualEvent),
      mergeMap(({ id }) =>
        service.delete(id).pipe(
          switchMap(() => [
            ManualEventsActions.deleteManualEventSuccess({ id }),
            SharedActions.showMessage({
              success: translate.instant(
                'TOASTS.MANUAL_EVENT_DELETED',
              ) as string,
            }),
          ]),
          catchError((response: unknown) => [
            ManualEventsActions.deleteManualEventFail(),
            SharedActions.showMessage({ error: errMessage(response as Error) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);
